import { ArrowRight, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import NetworkVisual from "@/components/NetworkVisual";

export default function Hero() {
  const navigate = useNavigate();

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid pointer-events-none" />
      <div className="absolute inset-0 bg-radial-fade pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-axiom-accent/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6 animate-fade-up">
              <span className="signal-dot animate-pulse-signal" />
              <span className="eyebrow">SIGNAL.LIVE / OPERATOR INTELLIGENCE</span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] leading-[1.04] animate-fade-up"
              style={{ animationDelay: "60ms" }}
            >
              Supply Chain <br className="hidden sm:block" />
              <span className="text-axiom-text">Clarity for Operators</span>
              <br />
              <span className="text-axiom-muted">Who Cannot Afford Noise.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-6 max-w-2xl text-base sm:text-lg text-axiom-muted leading-relaxed animate-fade-up"
              style={{ animationDelay: "140ms" }}
            >
              Axiom-AI helps companies identify hidden cost, network friction, and
              decision failure inside complex supply chains — before those costs
              become financial damage.
            </p>

            <div
              className="mt-9 flex flex-col sm:flex-row gap-3 animate-fade-up"
              style={{ animationDelay: "220ms" }}
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => navigate("/contact")}
                className="btn-axiom"
              >
                Request a Strategic Review
                <ArrowRight size={16} />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("framework")}
                className="btn-axiom-outline"
              >
                Explore the Axiom-AI Framework
                <ChevronRight size={16} />
              </button>
            </div>

            <p
              className="mt-10 max-w-xl text-sm text-axiom-dim leading-relaxed border-l border-white/10 pl-4 animate-fade-up"
              style={{ animationDelay: "320ms" }}
            >
              Operator-grade intelligence for transportation, distribution,
              forecasting, visibility, and cost-to-serve complexity.
            </p>

            {/* Micro stats */}
            <div
              className="mt-12 grid grid-cols-3 gap-6 max-w-xl animate-fade-up"
              style={{ animationDelay: "400ms" }}
            >
              {[
                { k: "20+", v: "Years operating experience" },
                { k: "40+", v: "DC network scale" },
                { k: "5", v: "Step diagnostic framework" },
              ].map((s) => (
                <div
                  key={s.v}
                  className="border-t border-white/10 pt-3"
                  data-testid={`hero-stat-${s.k.replace(/\W/g, "")}`}
                >
                  <div className="font-display text-2xl text-axiom-text">{s.k}</div>
                  <div className="text-[11px] uppercase tracking-wider text-axiom-dim mt-1 leading-snug">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: network visual */}
          <div className="lg:col-span-5">
            <div className="relative aspect-square w-full max-w-[520px] mx-auto p-4 sm:p-6 border border-white/10 bg-axiom-surface/40 backdrop-blur-sm">
              <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="signal-dot" style={{ width: 6, height: 6 }} />
                  <span className="num-label">AXM.NET / v4.2</span>
                </div>
                <span className="num-label">LIVE TRACE</span>
              </div>
              <NetworkVisual />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between num-label">
                <span>NODES.09 / LANES.05</span>
                <span>SIGNAL.42</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 num-label hidden md:flex items-center gap-2">
        <span>SCROLL</span>
        <div className="w-12 h-px bg-white/20" />
      </div>
    </section>
  );
}
